import {LHQInstance} from "./Scene.js";
import {useMemo, useEffect, useState, useCallback} from "react";
import * as THREE from "three";
import {useGLTF_fixed} from "../../../../Utils/ThreeJS/3JSHooks.js";
import {IfMaterialIsEmptyThenChangeColorToX, PrepForFastRaycasting, StandardMat_FallbackColor, StandardMeshFix} from "../Utils/ModelProcessor.js";
import {ChangeMaterialsToSupportPainting} from "../Utils/PaintPrepper.js";
import {Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import React from "react";
import {Assert} from "js-vextensions";
import {GetUVBounds, NormalizeUVsToFillUVSpace} from "../../../../Utils/ThreeJS/General.js";
import {autorun} from "mobx";
import {LoadBakedGLTF} from "./BakedLightmaps.js";
import {ApplyPlacement, FindPlacement, ModelPathToPlacementFileKey, objPlacementsStore, ObjPlacement, RegisterPlacementObject, UnregisterPlacementObject} from "./ObjPlacements.js";

export class UseSimple3DComp_Options {
	constructor(data?: RequiredBy<Partial<UseSimple3DComp_Options>, "path">) { Object.assign(this, data); }

	path: string;
	/** If true, `path` points to an LHQ_Web export (see Scripts/Blender), and the model uses its Blender-baked lightmaps (unlit; not part of the runtime ProgressiveLightMap/paint systems). */
	baked = false;
	castShadows = true;
	receiveShadows = true;

	allowMaterialChanging = true;
	//ensureHasMaterial = true;
	textureRescaling = 1;
	/** If true, all objects in this model have a normalized copy of their UVs used for the lightmap. (main purpose is to make objects with "tiled textures" compatible, but it may also improve packing density in the shared uv-space) */
	useNormalizedUVsForLightmap = true;

	position: Vec3 = new Vec3(0, 0, 0);
	rotation: [number, number, number] = [0, 0, 0];

	nodeProcessing_extraFilter?: (node: THREE.Object3D<THREE.Object3DEventMap>)=>boolean;

	// extra processing funcs
	extraProcessing_early?: (mesh: THREE.Mesh)=>void;
	extraProcessing_mid?: (mesh: THREE.Mesh)=>void;
	extraProcessing_late?: (mesh: THREE.Mesh)=>void;
}

export function useSimple3DComp(lhq: LHQInstance, options: ConstructorParameters<typeof UseSimple3DComp_Options>[0]) {
	const opt = new UseSimple3DComp_Options(options);
	const name = opt.path.split("/").pop()?.split(".").slice(0, -1).join(".") ?? "<unknown>"; // eg. "/3D/ZUp/Floor_1.0.glb" -> "Floor_1.0"
	const {scene} = useGLTF_forOptions(opt, lhq);

	const [objectsFinal, setObjectsFinal] = useInLHQLightmap(lhq, name);
	/** Resolves the sub-object-path for an object, relative to the model root (eg. "Chair_Left"; nested paths get joined with "/"). */
	const GetSubObjectPath = useCallback((obj: THREE.Object3D)=>{
		const subPathSegments = [] as string[];
		for (let node: THREE.Object3D|n = obj; node != null && node != scene; node = node.parent) {
			subPathSegments.unshift(node.name);
		}
		return subPathSegments.join("/");
	}, [scene]);

	const ApplyPlacements = useCallback((objects: THREE.Object3D[])=>{
		for (const obj of objects) {
			const placement = FindPlacement(opt.path, GetSubObjectPath(obj));
			ApplyPlacement(obj, placement);
		}
	}, [scene, opt.path, GetSubObjectPath]);

	// subscribe to the placements-map (deeply observable), re-applying placements whenever an entry relevant to this model is added/deleted/changed (lets the placements-dialog live-update the scene)
	// note: autorun is used (rather than reading the map during render) because this is a plain hook, not an observer-wrapped component
	const [groupPlacement, setGroupPlacement] = useState<ObjPlacement>({});
	const placementsFileKey = ModelPathToPlacementFileKey(opt.path);
	useEffect(()=>{
		if (scene == null) return;
		const disposer = autorun(()=>{
			// touch the map's relevant entries (and each of their fields), so this autorun re-runs on any add/delete/field-change among them
			const map = objPlacementsStore.map;
			for (const key of Object.keys(map)) {
				if (key == placementsFileKey || key.startsWith(`${placementsFileKey}/`)) {
					const entry = map[key];
					void (entry.position, entry.rotation, entry.scale);
				}
			}
			if (objectsFinal.length > 0) ApplyPlacements(objectsFinal);
			setGroupPlacement(FindPlacement(opt.path)); // for the group-level transform below
		});
		return disposer;
	}, [scene, objectsFinal, ApplyPlacements, placementsFileKey, opt.path]);

	useEffect(()=>{
		if (scene == null) return;

		scene.updateMatrixWorld(true); // ensure .matrixWorld fields are up-to-date before the transform-baking below

		const newObjectsFinal = [] as THREE.Mesh[];
		scene.traverse(node=>{
			if (opt.nodeProcessing_extraFilter?.(node) === false) return;

			if (node["isMesh"]) {
				const mesh = node as THREE.Mesh;

				// Bake the transforms of all intermediate group-nodes (between the mesh and the model-root) into the mesh itself.
				// This is required because only the meshes get mounted below (via <primitive>); the intermediate groups get dropped from the render-tree.
				// For single-primitive nodes this is a no-op (the mesh-node itself carries the transform), but for meshes which the glTF-pipeline
				// split into sub-meshes per material (eg. "GlassWall_Back" -> "GlassWall_Back_1"/"GlassWall_Back_2"), the source-object's transform
				// lives on the parent group-node instead -- so without this baking, the split parts (whose local transforms are all identity/0) collapse to position 0.
				if (mesh.parent != null && mesh.parent != scene) {
					mesh.matrixWorld.decompose(mesh.position, mesh.quaternion, mesh.scale);
				}

				opt.extraProcessing_early?.(mesh);

				// baked models: materials were already converted in LoadBakedGLTF; skip the runtime-lightmap/paint prep
				if (opt.baked) {
					mesh.castShadow = false;
					mesh.receiveShadow = false;
					opt.extraProcessing_mid?.(mesh);
					opt.extraProcessing_late?.(mesh);
					newObjectsFinal.push(mesh);
					return;
				}

				mesh.castShadow = opt.castShadows;
				mesh.receiveShadow = opt.receiveShadows;

				//FloorMeshFix(mesh);
				//StandardMeshFix(mesh);
				if (opt.allowMaterialChanging) {
					IfMaterialIsEmptyThenChangeColorToX(mesh.material, StandardMat_FallbackColor());
					if (lhq.usePaint || opt.textureRescaling != 1) {
						ChangeMaterialsToSupportPainting(mesh.material, opt.textureRescaling);
					}
				}
				if (lhq.usePaint) PrepForFastRaycasting(mesh);

				opt.extraProcessing_mid?.(mesh);

				if (opt.useNormalizedUVsForLightmap) {
					const uvs_normalized = mesh.geometry.attributes.uv.clone();
					NormalizeUVsToFillUVSpace(uvs_normalized, GetUVBounds(uvs_normalized));
					mesh["uvsForLightmap"] = uvs_normalized; // store the normalized UVs on the mesh for later use (eg. for lightmap baking)
				}

				opt.extraProcessing_late?.(mesh);

				console.log(`Added(${mesh.name}):`, mesh);

				lhq.lightmapObjects.push(mesh);
				newObjectsFinal.push(mesh);
			}
		});
		setObjectsFinal(newObjectsFinal);
	}, [scene]);

	// register the loaded objects in the placements-dialog registry (letting it show a tree-view of all objects with entries), and unregister on unmount
	useEffect(()=>{
		if (objectsFinal.length == 0) return;
		for (const obj of objectsFinal) {
			RegisterPlacementObject(opt.path, GetSubObjectPath(obj), obj);
		}
		return ()=> {
			for (const obj of objectsFinal) {
				UnregisterPlacementObject(opt.path, GetSubObjectPath(obj));
			}
		};
	}, [objectsFinal, opt.path, GetSubObjectPath]);

	// apply the group-level placement (file-level entry, if one exists) — this overrides opt.position when present
	const groupPosition = groupPlacement.position ? new Vec3(...groupPlacement.position) : opt.position;
	const groupRotation = groupPlacement.rotation ?? opt.rotation;
	const groupScale = groupPlacement.scale != null ? (typeof groupPlacement.scale == "number" ? [groupPlacement.scale, groupPlacement.scale, groupPlacement.scale] as const : groupPlacement.scale) : undefined;

	/** Loads the model at opt.path; baked models go through the KTX2/meshopt-capable, sequential LoadBakedGLTF. */
	function useGLTF_forOptions(opt: UseSimple3DComp_Options, lhq: LHQInstance) {
		const [bakedScene, setBakedScene] = useState<THREE.Group|n>(null);
		const renderer = lhq.renderer;
		useEffect(()=>{
			if (!opt.baked || renderer == null) return;
			let cancelled = false;
			LoadBakedGLTF(opt.path, renderer).then(gltf=>{ if (!cancelled) setBakedScene(gltf.scene); }, err=>console.error(`Failed to load baked model ${opt.path}:`, err));
			return ()=>{ cancelled = true; };
		}, [opt.baked, opt.path, renderer]);
		const regular = useGLTF_fixed(opt.baked ? null : opt.path);
		return opt.baked ? {scene: bakedScene} : regular;
	}

	if (objectsFinal.length == 0) return null;
	const ui = (
		<group position={groupPosition.To3JPos()} rotation={groupRotation} scale={groupScale}>
			{objectsFinal.map((obj, i)=><primitive key={i} object={obj}/>)}
		</group>
	);
	return ui;
}

export function useInLHQLightmap(lhq: LHQInstance, name?: string) {
	const [objectsFinal, setObjectsFinal] = useState<THREE.Mesh[]>([]);

	const id = useMemo(()=>name ?? Math.random().toString(), [name]);
	const setObjectFinal_proxy = useCallback((newObjectsFinal: THREE.Mesh[])=>{
		lhq.markModelLoadState(id, "loaded");
		console.log("Model loaded:", name);
		setObjectsFinal(newObjectsFinal);
	}, [setObjectsFinal]);

	useEffect(()=>{
		// check this first (in case of theoretical race-conditions/late-calling of useEffect callback)
		if (lhq.modelLoadStates.get(id) != "loaded") {
			lhq.markModelLoadState(id, "loading");
			console.log("Model loading:", name);
		}
		console.log("Model removed:", name);
		return lhq.markModelLoadState(id, "removed");
	}, []);

	return [objectsFinal, setObjectFinal_proxy] as const;
}